import React, { useState } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, MessageCircle, Send, CheckCircle2, AlertCircle, Building2, FileText } from "lucide-react";
import SplitPageHero from "../components/SplitPageHero";
import Reveal from "../components/Reveal";
import { Button } from "../components/Button";
import { COMPANY } from "../data";

const CONTACT_ENDPOINT = process.env.REACT_APP_CONTACT_ENDPOINT || "/send_mail.php";

const CORP_MAP_SRC =
  "https://www.google.com/maps?q=" +
  encodeURIComponent(COMPANY.offices.corporate.mapsQuery) +
  "&t=&z=15&ie=UTF8&iwloc=&output=embed";

const CORP_MAP_LINK =
  "https://www.google.com/maps/search/?api=1&query=" +
  encodeURIComponent(COMPANY.offices.corporate.mapsQuery);

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    message: "",
  });
  const [status, setStatus] = useState({ state: "idle", msg: "" });
  const [website, setWebsite] = useState("");

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const openWhatsapp = (payload) => {
    const text = encodeURIComponent(
      `Hello MRL Transports,\n\n` +
      `New enquiry from the website:\n` +
      `• Name: ${payload.name}\n` +
      `• Email: ${payload.email}\n` +
      `• Phone: ${payload.phone}\n` +
      (payload.company ? `• Company: ${payload.company}\n` : "") +
      `\nMessage:\n${payload.message}`
    );
    const url = `https://wa.me/${COMPANY.phones[0].raw}?text=${text}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ state: "loading", msg: "" });
    try {
      await axios.post(CONTACT_ENDPOINT, { ...form, website });
      setStatus({ state: "success", msg: "Thank you! Your enquiry has been received. Our team will contact you shortly. Opening WhatsApp so you can also send us a quick message." });
      openWhatsapp(form);
      setForm({ name: "", email: "", phone: "", company: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.message || err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || err?.message || "Something went wrong.";
      setStatus({ state: "error", msg: typeof detail === "string" ? detail : "Please check your inputs and try again." });
    }
  };

  return (
    <div data-testid="contact-page">
      <SplitPageHero
        eyebrow="Get In Touch"
        titleGreen="Contact Us."
        titleOrange="Plan your project."
        subtitle="For transportation requirements, fleet availability and project discussions — talk to our team today."
        imageWebp="/hero-contact.webp"
        imagePng="/hero-contact.png"
        imageAlt="MRL Transports operations control room with fleet map and dispatch screens"
        badgeLabel="24 / 7 Ops"
        badgeSub="Namakkal Control"
      />

      {/* MAIN */}
      <section className="py-20 md:py-28 bg-white" data-testid="contact-main">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Info column */}
          <aside className="lg:col-span-5 space-y-8">
            <Reveal>
              <div>
                <p className="section-tag">Reach Us</p>
                <h2 className="font-heading text-4xl md:text-5xl leading-[0.95] tracking-tightest mt-4 text-brand-ink">
                  Let's plan your next move.
                </h2>
              </div>
            </Reveal>

            <Reveal delay={100}>
              <div className="border-2 border-zinc-200 p-8 space-y-6" data-testid="contact-info-card">
                {COMPANY.phones.map((p) => (
                  <div key={p.raw} className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-brand-green shrink-0">
                      <Phone className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-heading text-xs uppercase tracking-widest text-brand-orange">{p.label}</p>
                      <a
                        href={`tel:${p.raw}`}
                        className="font-body text-base text-brand-ink mt-1 block hover:text-brand-green"
                        data-testid={`contact-phone-link-${p.raw}`}
                      >
                        {p.value}
                      </a>
                    </div>
                  </div>
                ))}

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-brand-green shrink-0">
                    <Mail className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-heading text-xs uppercase tracking-widest text-brand-orange">Email</p>
                    <a
                      href={`mailto:${COMPANY.email}`}
                      className="font-body text-base text-brand-ink mt-1 block hover:text-brand-green break-all"
                      data-testid="contact-email-link"
                    >
                      {COMPANY.email}
                    </a>
                  </div>
                </div>

                <a
                  href={`https://wa.me/${COMPANY.phones[0].raw}?text=${encodeURIComponent("Hello MRL Transports")}`}
                  target="_blank"
                  rel="noreferrer"
                  className="w-full flex items-center justify-center gap-2 bg-[#25D366] text-white font-heading uppercase tracking-widest text-xs px-6 py-4 hover:opacity-90"
                  data-testid="contact-whatsapp-btn"
                >
                  <MessageCircle className="w-4 h-4" /> Chat on WhatsApp
                </a>
              </div>
            </Reveal>
          </aside>

          {/* Form column */}
          <div className="lg:col-span-7">
            <Reveal>
              <div className="bg-brand-smoke p-8 md:p-12 border-t-4 border-brand-orange" data-testid="contact-form-wrap">
                <p className="font-mono text-xs uppercase tracking-widest text-brand-orange">Request a Quote</p>
                <h3 className="font-heading text-3xl md:text-4xl leading-tight tracking-tight mt-3 text-brand-ink">
                  Share your requirement.
                </h3>
                <p className="font-body text-base text-zinc-600 mt-3">
                  Our operations team responds within one business day with feasibility inputs and pricing.
                </p>

                <form onSubmit={submit} className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="contact-form">
                  <div className="absolute -left-[10000px]" aria-hidden="true">
                    <label htmlFor="contact-website">Website</label>
                    <input id="contact-website" name="website" tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} />
                  </div>
                  <Field label="Full Name *" testId="contact-input-name">
                    <input required value={form.name} onChange={update("name")} data-testid="contact-input-name-field"
                      className="w-full bg-white border-2 border-zinc-300 focus:border-brand-green outline-none px-4 py-3 font-body" />
                  </Field>
                  <Field label="Email *" testId="contact-input-email">
                    <input required type="email" value={form.email} onChange={update("email")} data-testid="contact-input-email-field"
                      className="w-full bg-white border-2 border-zinc-300 focus:border-brand-green outline-none px-4 py-3 font-body" />
                  </Field>
                  <Field label="Phone *" testId="contact-input-phone">
                    <input required value={form.phone} onChange={update("phone")} data-testid="contact-input-phone-field"
                      className="w-full bg-white border-2 border-zinc-300 focus:border-brand-green outline-none px-4 py-3 font-body" />
                  </Field>
                  <Field label="Company" testId="contact-input-company">
                    <input value={form.company} onChange={update("company")} data-testid="contact-input-company-field"
                      className="w-full bg-white border-2 border-zinc-300 focus:border-brand-green outline-none px-4 py-3 font-body" />
                  </Field>
                  <div className="md:col-span-2">
                    <Field label="Message *" testId="contact-input-message">
                      <textarea required rows={6} value={form.message} onChange={update("message")} data-testid="contact-input-message-field"
                        placeholder="Tell us about your project — load type, dimensions, origin, destination, timelines…"
                        className="w-full bg-white border-2 border-zinc-300 focus:border-brand-green outline-none px-4 py-3 font-body resize-y" />
                    </Field>
                  </div>

                  <div className="md:col-span-2 flex flex-col sm:flex-row items-stretch sm:items-center gap-4 mt-2">
                    <Button type="submit" variant="primary" size="lg" className="sm:min-w-[220px]" data-testid="contact-submit-btn" disabled={status.state === "loading"}>
                      {status.state === "loading" ? "Sending…" : (<>Send Enquiry <Send className="w-4 h-4" /></>)}
                    </Button>
                    <p className="font-mono text-[11px] uppercase tracking-widest text-zinc-500">
                      Also sends a WhatsApp draft to our operations team.
                    </p>
                  </div>

                  {status.state === "success" && (
                    <div className="md:col-span-2 border-2 border-brand-green bg-white p-4 flex items-start gap-3" data-testid="contact-success">
                      <CheckCircle2 className="w-5 h-5 text-brand-green mt-0.5" />
                      <p className="font-body text-sm text-brand-ink">{status.msg}</p>
                    </div>
                  )}
                  {status.state === "error" && (
                    <div className="md:col-span-2 border-2 border-red-500 bg-white p-4 flex items-start gap-3" data-testid="contact-error">
                      <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
                      <p className="font-body text-sm text-red-600">{status.msg}</p>
                    </div>
                  )}
                </form>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* OFFICES */}
      <section className="py-20 md:py-28 bg-brand-smoke" data-testid="contact-offices">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="mb-14">
            <Reveal><p className="section-tag">Our Offices</p></Reveal>
            <Reveal delay={100}>
              <h2 className="font-heading text-4xl md:text-5xl xl:text-6xl leading-[1.02] tracking-tight mt-4 text-brand-ink">
                Two offices. One committed team.
              </h2>
            </Reveal>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Corporate Office (with map) */}
            <Reveal className="lg:col-span-8">
              <div className="bg-white border-2 border-zinc-200 overflow-hidden h-full flex flex-col" data-testid="corporate-office-card">
                <div className="p-8 md:p-10 flex items-start gap-5">
                  <div className="w-14 h-14 shrink-0 flex items-center justify-center bg-brand-green">
                    <Building2 className="w-7 h-7 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-3">
                      <p className="font-heading text-[11px] uppercase tracking-widest text-brand-orange">
                        {COMPANY.offices.corporate.label}
                      </p>
                      <span className="inline-block bg-brand-green text-white text-[10px] font-mono uppercase tracking-widest px-2 py-1">
                        Primary
                      </span>
                    </div>
                    <p className="font-heading text-xl md:text-2xl mt-3 text-brand-ink leading-tight tracking-tight">
                      {COMPANY.offices.corporate.line1}
                    </p>
                    <p className="font-body text-sm md:text-base text-zinc-700 mt-1">
                      {COMPANY.offices.corporate.line2}
                    </p>
                    <p className="font-body text-sm md:text-base text-zinc-700">
                      {COMPANY.offices.corporate.city}
                    </p>
                    <a
                      href={CORP_MAP_LINK}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 mt-4 font-heading text-xs uppercase tracking-widest text-brand-green hover:text-brand-orange"
                      data-testid="corporate-office-directions"
                    >
                      <MapPin className="w-4 h-4" /> Get Directions
                    </a>
                  </div>
                </div>
                <div className="aspect-[16/9] w-full border-t border-zinc-200">
                  <iframe
                    title="MRL Transports Corporate Office Location"
                    src={CORP_MAP_SRC}
                    className="w-full h-full border-0"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    data-testid="contact-map-iframe"
                  />
                </div>
              </div>
            </Reveal>

            {/* Registered Office (address only) */}
            <Reveal delay={150} className="lg:col-span-4">
              <div className="bg-white border-2 border-zinc-200 p-8 md:p-10 h-full" data-testid="registered-office-card">
                <div className="w-14 h-14 flex items-center justify-center bg-brand-orange">
                  <FileText className="w-7 h-7 text-white" />
                </div>
                <p className="font-heading text-[11px] uppercase tracking-widest text-brand-orange mt-6">
                  {COMPANY.offices.registered.label}
                </p>
                <p className="font-heading text-xl md:text-2xl mt-3 text-brand-ink leading-tight tracking-tight">
                  {COMPANY.offices.registered.line1}
                </p>
                <p className="font-body text-sm md:text-base text-zinc-700 mt-1">
                  {COMPANY.offices.registered.line2}
                </p>
                <p className="font-body text-sm md:text-base text-zinc-700">
                  {COMPANY.offices.registered.city}
                </p>

                <div className="mt-8 border-t border-zinc-200 pt-6">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                    Correspondence & statutory records only.
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, testId, children }) {
  return (
    <label className="block" data-testid={testId}>
      <span className="font-mono text-[11px] uppercase tracking-widest text-brand-ink block mb-2">{label}</span>
      {children}
    </label>
  );
}
